import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import { ConfirmModal, StateView, type MenuAction } from '@/card/record'
import { RecordPicker } from '@/card/RecordPicker'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  Grip,
  LayerButtons,
  Notice,
  OnOffBadge,
  PrimaryButton,
  RowMenu,
  SkeletonList,
  Switch,
  Tokens,
  initials,
  isForbidden,
  moveId,
  useAdminEditing,
} from './AdminShell'
import {
  createLeadRule,
  deleteLeadRule,
  fetchLeadRules,
  updateLeadRule,
  type AdminLeadRule,
  type RuleStrategy,
} from './api'

/**
 * Правила распределения лидов (тикет #103, макет AdmRouting.dc.html).
 *
 * Правила проверяются сверху вниз — по возрастанию приоритета, — и первое подошедшее
 * назначает ответственного; порядок на экране и есть приоритет, перестановка его
 * сохраняет. Условия — только по полям, которые распределитель умеет читать: произвольный
 * ключ сохранился бы и молча не совпал ни с одним лидом, поэтому формы для него нет, а
 * сервер его отвергает. Сотрудники выбираются поиском — в правиле важен порядок (по кругу
 * идут именно в нём), и список показан в этом порядке.
 *
 * Счётчиков «сработало за 30 дней», числа лидов в общей очереди и учёта рабочего
 * календаря правило не хранит и не отдаёт — на экране их нет.
 */

const STRATEGIES: RuleStrategy[] = ['round_robin', 'by_load', 'by_region', 'manual']
const STRATEGY_ICON: Record<RuleStrategy, IconName> = {
  round_robin: 'adm-queue',
  by_load: 'adm-load',
  by_region: 'flag',
  manual: 'user',
}
const SOURCES = ['website', 'call', 'email', 'telegram', 'referral', 'exhibition', 'other']
const FIELDS = ['source', 'utmSource', 'utmCampaign', 'companyName', 'scoreClass'] as const
/**
 * Оценка лида (#129): один класс или «горячие и тёплые». Сервер принимает список через запятую;
 * экран предлагает только осмысленные наборы — «холодные и горячие» без тёплых правилом не
 * задаётся, и путать администратора таким вариантом незачем.
 */
const SCORE_CLASSES = ['hot', 'hot,warm', 'warm', 'cold'] as const

/** «Горячий» или «Горячий, Тёплый» — значение условия словами. */
function scoreClassText(t: (key: string) => string, value: string): string {
  return value.split(',').map((code) => t(`value.sales.lead.score_class.${code.trim()}`)).join(', ')
}

export function LeadRulesPage() {
  const { t } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const rules = useQuery({ queryKey: ['admin-lead-rules'], queryFn: ({ signal }) => fetchLeadRules(signal) })
  const [draft, setDraft] = useState<RuleDraft | null>(null)
  const [removing, setRemoving] = useState<AdminLeadRule | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)

  const refresh = () => {
    editing.clear()
    void queryClient.invalidateQueries({ queryKey: ['admin-lead-rules'] })
  }
  const toggle = useMutation({
    mutationFn: (rule: AdminLeadRule) => updateLeadRule(rule.id, { active: !rule.active }),
    onSuccess: refresh,
    onError: editing.fail,
  })
  const remove = useMutation({
    mutationFn: deleteLeadRule,
    onSuccess: () => {
      setRemoving(null)
      refresh()
    },
    onError: (error) => {
      setRemoving(null)
      editing.fail(error)
    },
  })
  const reorder = useMutation({
    mutationFn: async (ordered: AdminLeadRule[]) => {
      for (const [index, rule] of ordered.entries()) {
        const priority = (index + 1) * 10
        if (rule.priority !== priority) await updateLeadRule(rule.id, { priority })
      }
    },
    onSuccess: refresh,
    onError: (error) => {
      editing.fail(error)
      void queryClient.invalidateQueries({ queryKey: ['admin-lead-rules'] })
    },
  })

  const list = [...(rules.data ?? [])].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name))
  const forbidden = isForbidden(rules.error)
  const move = (id: string, target: number) => {
    const ids = moveId(list.map((rule) => rule.id), id, target)
    if (ids.join() === list.map((rule) => rule.id).join()) return
    reorder.mutate(ids.map((value) => list.find((rule) => rule.id === value)!))
  }
  const nextPriority = list.length === 0 ? 10 : Math.max(...list.map((rule) => rule.priority)) + 10

  const menu = (rule: AdminLeadRule): MenuAction[] => [
    { id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setDraft(draftOf(rule)) },
    { id: 'delete', label: t('admin.rule.delete'), icon: 'trash', danger: true, onSelect: () => setRemoving(rule) },
  ]

  return (
    <AdminPage
      titleKey="adm.rule.title"
      leadKey="adm.rule.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.rule.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && <PrimaryButton label={t('admin.rule.add')} onClick={() => setDraft({ ...EMPTY, priority: String(nextPriority) })} />}
    >
      <Notice text={editing.notice} />
      {!forbidden && !rules.error && (
        <div className="alert alert-info">
          <Icon name="adm-info" />
          <span><span className="strong">{t('adm.rule.order.title')}</span> {t('adm.rule.order.text')}</span>
        </div>
      )}
      <AdminState
        isLoading={rules.isLoading}
        error={rules.error}
        onRetry={() => void rules.refetch()}
        forbiddenTitleKey="adm.rule.forbidden"
        errorTitleKey="adm.rule.error"
        skeleton={<SkeletonList rows={5} />}
      >
        {list.length === 0 && (
          <StateView boxed kind="empty" title={t('adm.rule.empty')} description={t('adm.rule.empty.text')}
                     actions={editing.mayEdit && (
                       <button type="button" className="btn btn-secondary btn-sm"
                               onClick={() => setDraft({ ...EMPTY, priority: String(nextPriority) })}>
                         <Icon name="plus" size="sm" />{t('admin.rule.add')}
                       </button>
                     )} />
        )}
        <div className="sort-list" role="list" aria-label={t('adm.rule.listLabel')} aria-busy={reorder.isPending || undefined}>
          <div className="sort-row adm-sort-head" aria-hidden="true">
            {editing.mayEdit && <span className="adm-w24" />}
            <span className="adm-w22">№</span>
            <span className="adm-grow">{t('adm.rule.head')}</span>
            <span className="adm-col" style={{ width: 200 }}>{t('admin.rule.strategy')}</span>
            <span className="adm-col" style={{ width: 150 }}>{t('adm.rule.members')}</span>
            <span className="adm-col" style={{ width: 110 }}>{t('adm.enabledColumn')}</span>
            {editing.mayEdit && <span className="adm-w26" />}
          </div>
          {list.map((rule, index) => {
            const names = rule.employees.map((e) => e.name).join(', ')
            const conditions = Object.entries(parseConditions(rule.conditions))
            return (
              <div
                key={rule.id}
                role="listitem"
                className={[
                  'sort-row',
                  rule.active ? '' : 'is-off',
                  dragging === rule.id ? 'is-dragging' : '',
                  draft?.id === rule.id ? 'is-selected' : '',
                ].filter(Boolean).join(' ')}
                style={{ minHeight: 56 }}
                draggable={editing.mayEdit}
                onDragStart={(event) => {
                  setDragging(rule.id)
                  event.dataTransfer?.setData('text/plain', rule.id)
                }}
                onDragEnd={() => setDragging(null)}
                onDragOver={(event) => {
                  if (editing.mayEdit && dragging) event.preventDefault()
                }}
                onDrop={(event) => {
                  event.preventDefault()
                  const id = event.dataTransfer?.getData('text/plain') || dragging
                  setDragging(null)
                  if (id && id !== rule.id) move(id, index)
                }}
              >
                {editing.mayEdit && (
                  <Grip label={t('adm.rule.move', { name: rule.name })} disabled={reorder.isPending}
                        onMove={(step) => move(rule.id, index + step)} />
                )}
                <span className="ord" style={editing.mayEdit ? undefined : { marginLeft: 8 }}>{index + 1}</span>
                <span className="adm-grow stack" style={{ gap: 4, padding: '4px 0' }}>
                  <span className={rule.active ? 'strong' : 'strong muted'}>{rule.name}</span>
                  {conditions.length === 0 ? (
                    <span className="t-sm muted">{t('admin.rule.anyLead')}</span>
                  ) : (
                    <Tokens join={t('adm.rule.and')} items={conditions.map(([key, value]) => ({
                      k: t(`admin.rule.field.${key}`),
                      op: '=',
                      v: key === 'source' ? t(`lead.source.${value}`)
                        : key === 'scoreClass' ? scoreClassText(t, value) : value,
                    }))} />
                  )}
                  {rule.departmentName && <span className="t-xs muted">{t('adm.rule.department', { name: rule.departmentName })}</span>}
                </span>
                <span className="adm-col" style={{ width: 200 }}>
                  <span className="badge"><Icon name={STRATEGY_ICON[rule.strategy]} />{t(`admin.rule.strategy.${rule.strategy}`)}</span>
                </span>
                <span className="adm-col row" style={{ width: 150, gap: 0 }} title={names}>
                  {rule.employees.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    <span className="adm-avas" aria-hidden="true">
                      {rule.employees.slice(0, 3).map((employee) => (
                        <span key={employee.id} className="ava">{initials(employee.name)}</span>
                      ))}
                    </span>
                  )}
                  {rule.employees.length > 3 && <span className="t-sm muted" style={{ marginLeft: 8 }}>{t('adm.rule.more', { count: rule.employees.length - 3 })}</span>}
                  {names && <span className="sr-only">{names}</span>}
                </span>
                <span className="adm-col" style={{ width: 110 }}>
                  {editing.mayEdit ? (
                    <Switch checked={rule.active} disabled={toggle.isPending} ariaLabel={t('adm.rule.enabledOf', { name: rule.name })}
                            onChange={() => toggle.mutate(rule)} />
                  ) : (
                    <OnOffBadge on={rule.active} />
                  )}
                </span>
                {editing.mayEdit && <RowMenu label={t('adm.rule.actions', { name: rule.name })} items={menu(rule)} />}
              </div>
            )
          })}
          <div className="sort-sep"><Icon name="lock" size="xs" />{t('adm.rule.queueSep')}</div>
          <div className="sort-row adm-queue" role="listitem">
            {editing.mayEdit && <Grip label={t('adm.rule.queuePinned')} locked />}
            <span className="ord" style={{ background: 'var(--surface)', color: 'var(--accent-text)', borderColor: 'var(--accent-border)' }}>
              <Icon name="adm-queue" size="xs" />
            </span>
            <span className="adm-grow stack" style={{ gap: 2 }}>
              <span className="row-wrap" style={{ gap: '6px 8px' }}>
                <span className="strong">{t('adm.rule.queue.title')}</span>
                <Icon name="arrow-right" size="sm" />
                <span className="strong" style={{ color: 'var(--accent-text)' }}>{t('adm.rule.queue.target')}</span>
              </span>
              <span className="t-sm secondary">{t('adm.rule.queue.text')}</span>
            </span>
          </div>
        </div>
      </AdminState>

      {draft && (
        <RuleSheet draft={draft} position={draft.id ? list.findIndex((rule) => rule.id === draft.id) + 1 : list.length + 1}
                   total={list.length + (draft.id ? 0 : 1)} onChange={setDraft} onClose={() => setDraft(null)}
                   onSaved={() => {
                     setDraft(null)
                     refresh()
                   }} />
      )}
      <ConfirmModal
        open={removing != null}
        title={t('adm.rule.deleteConfirm', { name: removing?.name ?? '' })}
        confirmLabel={t('admin.rule.delete')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      >
        <p>{t('adm.rule.deleteConfirm.text')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

interface RuleDraft {
  id: string | null
  name: string
  strategy: RuleStrategy
  /** Ordered conditions: the order in which the administrator wrote them. */
  conditions: { key: string; value: string }[]
  department: { id: string; label: string } | null
  employees: { id: string; label: string }[]
  priority: string
  active: boolean
}

const EMPTY: RuleDraft = {
  id: null, name: '', strategy: 'round_robin', conditions: [], department: null, employees: [], priority: '0', active: true,
}

function parseConditions(raw: string): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      return Object.fromEntries(Object.entries(parsed as Record<string, unknown>).map(([k, v]) => [k, String(v)]))
    }
  } catch {
    // A malformed stored value is shown as "no conditions" in the form; saving replaces it.
  }
  return {}
}

const draftOf = (rule: AdminLeadRule): RuleDraft => ({
  id: rule.id,
  name: rule.name,
  strategy: rule.strategy,
  conditions: Object.entries(parseConditions(rule.conditions)).map(([key, value]) => ({ key, value })),
  department: rule.departmentId ? { id: rule.departmentId, label: rule.departmentName ?? rule.departmentId } : null,
  employees: rule.employees.map((e) => ({ id: e.id, label: e.name })),
  priority: String(rule.priority),
  active: rule.active,
})

function RuleSheet({
  draft,
  position,
  total,
  onChange,
  onClose,
  onSaved,
}: {
  draft: RuleDraft
  position: number
  total: number
  onChange: (draft: RuleDraft) => void
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name,
        strategy: draft.strategy,
        // An empty value is no condition at all, not "matches an empty field".
        conditions: Object.fromEntries(draft.conditions.filter((c) => c.value.trim() !== '').map((c) => [c.key, c.value.trim()])),
        departmentId: draft.department?.id ?? null,
        employeeIds: draft.employees.map((e) => e.id),
        priority: Number(draft.priority || '0'),
        active: draft.active,
      }
      return draft.id ? updateLeadRule(draft.id, body) : createLeadRule(body)
    },
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  const used = new Set(draft.conditions.map((c) => c.key))
  const free = FIELDS.filter((field) => !used.has(field))
  const setCondition = (index: number, patch: Partial<{ key: string; value: string }>) =>
    onChange({ ...draft, conditions: draft.conditions.map((c, at) => (at === index ? { ...c, ...patch } : c)) })

  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t('adm.rule.overline', { n: position, of: total })}
      title={draft.id ? draft.name : t('admin.rule.add')}
      onClose={onClose}
      hint={t('adm.rule.appliesToNew')}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                            disabled={draft.name.trim() === ''} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('admin.rule.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('admin.rule.name')} htmlFor="rl-name" required>
          <input id="rl-name" className="input" value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
        </Field>
        <div className="field" style={{ gap: 6 }}>
          <span className="label">{t('adm.rule.conditionsAll')}</span>
          {draft.conditions.length === 0 && <span className="t-sm muted">{t('adm.rule.noConditions')}</span>}
          {draft.conditions.map((condition, index) => (
            <div className="adm-cond" key={`${condition.key}-${index}`}>
              <span className="fb-join">{index === 0 ? t('adm.thresholds.if') : t('adm.rule.and')}</span>
              <select className="input input-sm" aria-label={t('adm.rule.conditionField', { n: index + 1 })} value={condition.key}
                      onChange={(e) => setCondition(index, { key: e.target.value, value: '' })}>
                {FIELDS.filter((field) => field === condition.key || !used.has(field)).map((field) => (
                  <option key={field} value={field}>{t(`admin.rule.field.${field}`)}</option>
                ))}
              </select>
              <span className="t-sm muted">{t('adm.rule.equals')}</span>
              {condition.key === 'source' ? (
                <select className="input input-sm" aria-label={t(`admin.rule.field.${condition.key}`)} value={condition.value}
                        onChange={(e) => setCondition(index, { value: e.target.value })}>
                  <option value="">—</option>
                  {SOURCES.map((source) => (
                    <option key={source} value={source}>{t(`lead.source.${source}`)}</option>
                  ))}
                </select>
              ) : condition.key === 'scoreClass' ? (
                <select className="input input-sm" aria-label={t(`admin.rule.field.${condition.key}`)} value={condition.value}
                        onChange={(e) => setCondition(index, { value: e.target.value })}>
                  <option value="">—</option>
                  {SCORE_CLASSES.map((value) => (
                    <option key={value} value={value}>{scoreClassText(t, value)}</option>
                  ))}
                </select>
              ) : (
                <input className="input input-sm" aria-label={t(`admin.rule.field.${condition.key}`)} value={condition.value}
                       onChange={(e) => setCondition(index, { value: e.target.value })} />
              )}
              <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('adm.rule.removeCondition', { n: index + 1 })}
                      onClick={() => onChange({ ...draft, conditions: draft.conditions.filter((_, at) => at !== index) })}>
                <Icon name="close" size="sm" />
              </button>
            </div>
          ))}
          <div className="row" style={{ paddingLeft: 46, gap: 8 }}>
            <button type="button" className="btn btn-ghost btn-sm" disabled={free.length === 0}
                    onClick={() => free[0] && onChange({ ...draft, conditions: [...draft.conditions, { key: free[0], value: '' }] })}>
              <Icon name="plus" size="sm" />{t('adm.rule.addCondition')}
            </button>
            <span className="t-xs muted">{t('adm.rule.fieldsHint')}</span>
          </div>
        </div>
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0, gap: 0 }}>
          <legend className="label" style={{ padding: 0, marginBottom: 6 }}>{t('admin.rule.strategy')}</legend>
          <div className="adm-radio">
            {STRATEGIES.map((strategy) => (
              <label key={strategy} className={draft.strategy === strategy ? 'is-on' : undefined}>
                <input type="radio" name={`${formId}-strategy`} checked={draft.strategy === strategy}
                       onChange={() => onChange({ ...draft, strategy })} />
                <span className="stack" style={{ gap: 0 }}>
                  <span className="strong">{t(`admin.rule.strategy.${strategy}`)}</span>
                  <span className="t-sm secondary">{t(`adm.rule.strategy.${strategy}.text`)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <Field label={t('admin.rule.department')} htmlFor="rule-department" help={t('adm.rule.department.help')}>
          <RecordPicker id="rule-department" entity="hr.department" searchColumn="name" value={draft.department}
                        onChange={(department) => onChange({ ...draft, department })} />
        </Field>
        <div className="field" style={{ gap: 6 }}>
          <span className="label">{t('adm.rule.membersCount', { count: draft.employees.length })}</span>
          {draft.employees.length > 0 && (
            <div className="panel" style={{ overflow: 'hidden' }}>
              <ol className="plain-list" style={{ margin: 0, padding: 0 }}>
                {draft.employees.map((employee) => (
                  <li key={employee.id} className="list-item" style={{ minHeight: 36, padding: '4px 6px 4px 12px' }}>
                    <span className="ava">{initials(employee.label)}</span>
                    <span className="adm-grow ellipsis" style={{ flex: 1 }}>{employee.label}</span>
                    <button type="button" className="btn btn-ghost btn-sm btn-icon"
                            aria-label={`${t('admin.rule.removeEmployee')} ${employee.label}`}
                            onClick={() => onChange({ ...draft, employees: draft.employees.filter((e) => e.id !== employee.id) })}>
                      <Icon name="close" size="sm" />
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <RecordPicker id="rule-employee" entity="hr.employee" searchColumn="last_name" extraColumn="first_name" value={null}
                        onChange={(picked) => {
                          if (picked && !draft.employees.some((e) => e.id === picked.id)) {
                            onChange({ ...draft, employees: [...draft.employees, picked] })
                          }
                        }} />
          <span className="help">{t('adm.rule.members.help')}</span>
        </div>
        <Switch checked={draft.active} label={t('adm.rule.activeLabel')} onChange={(active) => onChange({ ...draft, active })} />
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
